import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, TextField } from '@mui/material'
import { Section } from 'components/controls/section'
import { checkEmailRegex, validationMessages } from 'constants/validation'

import { InviteRequestFormData } from 'interface/user/invite/types'
import { flatten } from 'shared/util'

const {
  Required,
  TooLongLength,
  TooShortLength,
  InvalidEMailAddress,
} = validationMessages

const formSchema = yup.object().shape({
  userName: yup.string()
    .required(Required)
    .min(1, TooShortLength(1))
    .max(20, TooLongLength(20)),
  email: yup
    .string()
    .required(Required)
    .max(512, TooLongLength(512))
    .matches(checkEmailRegex, InvalidEMailAddress),
  companyName: yup.string(),
  department: yup.string(),
  purposeOfUse: yup.string()
    .required(Required),
  comment: yup.string()
    .required(Required)
    .min(1, TooShortLength(1))
    .max(20, TooLongLength(20)),
})
type FormType = yup.InferType<typeof formSchema>

export const useInviteRequestForm = (
  domain: string
) => {
  const form = useForm({
    defaultValues: {
      userName: '',
      email: '',
      companyName: '',
      department: '',
      purposeOfUse: '',
      comment: ''
    },
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(formSchema)
  })
  const submit = useCallback(
		(input: FormType): InviteRequestFormData => ({
      userName: input.userName,
      email: input.email,
      comment: flatten([
        input.companyName ? `- 社名: ${input.companyName}` : null,
        input.department ? `- 部署: ${input.department}` : null,
        input.purposeOfUse ? `- 利用目的: ${input.purposeOfUse}` : null,
        input.comment ? `- コメント: ${input.comment}` : null
      ]).join('\n')
    }),
    []
  )
  return {
    domain,
    form,
    submit
  }
}

export const InviteRequestForm = ({
  form: {
    control,
    formState: { errors },
  },
}: ReturnType<typeof useInviteRequestForm>) => {
  return (
    <Stack direction="column">
      <Section label="ユーザー名" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="userName"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="ユーザー名"
              size="small"
              error={!!errors.userName}
              helperText={errors.userName ? errors.userName.message : ''}
            />
          )}
        />
      </Section>

      <Section label="メールアドレス" labelProps={{ py: 2 }}>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <TextField
              placeholder="メールアドレス"
              {...field}
              variant="outlined"
              size="small"
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : ''}
            />
          )}
        />
      </Section>

      <Section label="会社名" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="companyName"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="会社名"
              size="small"
              error={!!errors.companyName}
              helperText={errors.companyName ? errors.companyName.message : ''}
            />
          )}
        />
      </Section>

      <Section label="部署名" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="department"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="部署名"
              size="small"
              error={!!errors.companyName}
              helperText={errors.companyName ? errors.companyName.message : ''}
            />
          )}
        />
      </Section>

      <Section label="利用目的" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="purposeOfUse"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="利用目的"
              size="small"
              multiline
              rows={5}
              error={!!errors.comment}
              helperText={errors.comment ? errors.comment.message : ''}
            />
          )}
        />
      </Section>

      <Section label="コメント" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="コメント"
              size="small"
              multiline
              rows={5}
              error={!!errors.comment}
              helperText={errors.comment ? errors.comment.message : ''}
            />
          )}
        />
      </Section>
    </Stack>
  )
}
